import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useToasts } from 'react-toast-notifications';
import { getFileType } from '../utilities/parsers';
import { uploadFileToOrder } from '../api/Api';

const UploadQueueContext = createContext({});
UploadQueueContext.displayName = 'UploadQueueContext';

export const useUploadQueueContext = () => {
  const context = useContext(UploadQueueContext);
  if (!context) throw new Error(`useUploadQueueContext must be used within UploadQueueProvider`);
  return context;
};

export const UploadQueueProvider = ({ children }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isModalMinimized, setIsModalMinimized] = useState(false);
  const { addToast } = useToasts();

  // Variables de entorno
  const FTP_MATERIALS_FOLDER = process.env.REACT_APP_ATTACHMENTS_FTP_MATERIALS_FOLDER;
  const SHARED_APP_IP_UPLOAD = process.env.REACT_APP_IP_UPLOAD;
  const MAX_FILE_SIZE_MB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 100; // Default 70MB

  useEffect(() => {
    console.log('📤 UploadQueueContext - montado/re-montado');
    return () => {
      console.log('📤 UploadQueueContext - desmontado');
    };
  }, []);

  // Validar tamaño de archivo
  const validateFileSize = (file) => {
    const fileSizeMB = file.size / (1024 * 1024); // Convertir bytes a MB
    return fileSizeMB <= MAX_FILE_SIZE_MB;
  };

  // Agregar upload a la cola
  const addToQueue = (uploadData) => {
    // Validar si hay espacio en la cola
    if (uploadQueue.length >= 2) {
      addToast('Solo se permiten máximo 2 cargas simultáneas. Intenta más tarde.', {
        appearance: 'warning',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
      return;
    }

    // Validar tamaño del archivo
    const file = uploadData.fileToUpload[0];
    const isFileSizeValid = validateFileSize(file);
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

    const newUpload = {
      id: Date.now() + Math.random(),
      status: isFileSizeValid ? 'pending' : 'error', // Si excede el tamaño, marcar como error inmediatamente
      createdAt: new Date(),
      completedAt: isFileSizeValid ? null : new Date(),
      error: isFileSizeValid ? null : `Archivo supera el tamaño máximo permitido (${MAX_FILE_SIZE_MB}MB). Tamaño actual: ${fileSizeMB}MB`,
      fileSizeMB: fileSizeMB,
      maxSizeMB: MAX_FILE_SIZE_MB,
      ...uploadData,
    };

    setUploadQueue((prev) => [...prev, newUpload]);
  };

  // Remover upload de la cola
  const removeFromQueue = (uploadId) => {
    setUploadQueue((prev) => prev.filter((upload) => upload.id !== uploadId));
  };

  // Actualizar estado del upload
  const updateUploadStatus = (uploadId, status, error = null) => {
    setUploadQueue((prev) =>
      prev.map((upload) =>
        upload.id === uploadId
          ? {
              ...upload,
              status,
              completedAt: status === 'success' || status === 'error' ? new Date() : null,
              error,
            }
          : upload
      )
    );
  };

  // Procesar upload individual
  const processUpload = async (upload) => {
    updateUploadStatus(upload.id, 'processing');

    try {
      const {
        fileToUpload,
        fileDescription,
        fileType,
        profile,
        parsedOrderNumber,
        orderId,
        getOrder,
      } = upload;
      const parsedMediaName = profile.media.name;

      const fileUrl = SHARED_APP_IP_UPLOAD;
      const formData = new FormData();
      formData.append('file', fileToUpload[0]);
      formData.append('material_folder', FTP_MATERIALS_FOLDER);
      formData.append('order_number', parsedOrderNumber);
      formData.append('media', parsedMediaName);
      formData.append('file_type', fileType);

      const response = await axios.post(fileUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Validar tipo de respuesta
      const contentType = response.headers['content-type'] || '';
      if (contentType.includes('text/html') || contentType.includes('text/plain')) {
        throw new Error('Nombre de archivo con caracteres especiales');
      }

      // Verificar si la respuesta es exitosa
      if (response.status === 200 || response.status === 201) {
        addToast('Archivo Cargado con éxito', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 1500,
          placement: 'bottom-center',
        });

        await uploadFileToOrder(orderId, fileToUpload[0], fileDescription, fileType, false);
        updateUploadStatus(upload.id, 'success');
      } else {
        throw new Error('Error en la carga HTTP');
      }
    } catch (error) {
      // Si el error es 413 (Request Entity Too Large), manejarlo específicamente
      if (error.response && error.response.status === 413) {
        updateUploadStatus(upload.id, 'error', `Archivo supera el tamaño máximo permitido (${MAX_FILE_SIZE_MB}MB)`);
        return;
      }

      try {
        addToast('Error en carga HTTP, intentando por FTP', {
          appearance: 'warning',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        });

        await uploadFileToOrder(
          upload.orderId,
          upload.fileToUpload[0],
          upload.fileDescription,
          upload.fileType,
          true
        );
        updateUploadStatus(upload.id, 'success');
      } catch (ftpError) {
        console.error('Error en upload:', ftpError);
        addToast('Error al cargar archivo. Contacte al administrador.', {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        });
        updateUploadStatus(upload.id, 'error', ftpError.message);
      }
    }

    // Refrescar datos si existe la función
    if (upload.getOrder) {
      await upload.getOrder();
    }
  };

  // Efecto para procesar la cola
  useEffect(() => {
    const processingUploads = uploadQueue.filter((upload) => upload.status === 'processing');
    const pendingUploads = uploadQueue.filter((upload) => upload.status === 'pending');
    const errorUploads = uploadQueue.filter((upload) => upload.status === 'error');

    // Actualizar estado uploading
    setUploading(processingUploads.length > 0 || pendingUploads.length > 0);

    // Mostrar modal si hay uploads (incluyendo errores) y no está visible
    if ((processingUploads.length > 0 || pendingUploads.length > 0 || errorUploads.length > 0) && !isModalVisible) {
      setIsModalVisible(true);
    }

    // Procesar uploads pendientes si hay espacio
    if (processingUploads.length < 2 && pendingUploads.length > 0) {
      const uploadToProcess = pendingUploads[0];
      processUpload(uploadToProcess);
    }
  }, [uploadQueue, isModalVisible]);

  // Efecto para limpiar uploads completados después de 10 segundos
  useEffect(() => {
    const completedUploads = uploadQueue.filter(
      (upload) => (upload.status === 'success' || upload.status === 'error') && upload.completedAt
    );

    if (completedUploads.length === 0) return;

    const interval = setInterval(() => {
      const currentTime = new Date();
      const uploadsToRemove = [];

      completedUploads.forEach((upload) => {
        const timeSinceCompletion = currentTime - new Date(upload.completedAt);
        if (timeSinceCompletion >= 10000) {
          // 10 segundos
          uploadsToRemove.push(upload.id);
        }
      });

      uploadsToRemove.forEach((id) => {
        removeFromQueue(id);
      });
    }, 1000); // Revisar cada segundo

    return () => clearInterval(interval);
  }, [uploadQueue]);

  const contextValue = {
    uploading,
    uploadQueue,
    isModalVisible,
    setIsModalVisible,
    isModalMinimized,
    setIsModalMinimized,
    addToQueue,
    removeFromQueue,
  };

  return <UploadQueueContext.Provider value={contextValue}>{children}</UploadQueueContext.Provider>;
};